/**
 * Tenant timezones.
 *
 * WHY THIS EXISTS: every schedule in the product was evaluated against
 * Asia/Kolkata, so a Gulf tenant's 17:00-19:00 happy hour fired at 15:30 local.
 * Promotional windows, restaurant happy hours, menu periods and shift lateness
 * were all wrong by the offset between IST and the tenant's actual clock.
 *
 * A tenant now carries its own `timezone`. This table supplies the DEFAULT for
 * that column and the picker's options — it is not itself the source of truth,
 * because a business may trade in a country whose currency it does not use.
 *
 * NONE of the seven launch countries observes daylight saving. That is what
 * makes a fixed offset a total solution here rather than an approximation, and
 * it is why `offsetMin` can be trusted for arithmetic. The IANA name is what
 * Postgres and Intl want; the offset is for display and for cheap comparisons
 * that must not allocate a formatter.
 *
 * Promoted from a file-local const in `apps/pos/src/pages/SettingsPage.tsx`,
 * which had the data right but could only reach one page.
 */

export interface TenantTimezone {
    /** IANA zone name — what Postgres `AT TIME ZONE` and `Intl` accept. */
    iana: string;
    /** Fixed minutes east of UTC. Safe because none of these observe DST. */
    offsetMin: number;
    /** Short label for the UI, e.g. "IST". */
    abbr: string;
}

/** The zone a tenant gets before anyone chooses one. */
export const DEFAULT_TIMEZONE = 'Asia/Kolkata';

/** Keyed by ISO country code, the same key `getCountryForCurrency` returns. */
export const COUNTRY_TIMEZONE: Readonly<Record<string, TenantTimezone>> = Object.freeze({
    IN: Object.freeze({ iana: 'Asia/Kolkata', offsetMin: 330, abbr: 'IST' }),
    AE: Object.freeze({ iana: 'Asia/Dubai', offsetMin: 240, abbr: 'GST' }),
    OM: Object.freeze({ iana: 'Asia/Muscat', offsetMin: 240, abbr: 'GST' }),
    SA: Object.freeze({ iana: 'Asia/Riyadh', offsetMin: 180, abbr: 'AST' }),
    QA: Object.freeze({ iana: 'Asia/Qatar', offsetMin: 180, abbr: 'AST' }),
    KW: Object.freeze({ iana: 'Asia/Kuwait', offsetMin: 180, abbr: 'AST' }),
    BH: Object.freeze({ iana: 'Asia/Bahrain', offsetMin: 180, abbr: 'AST' }),
});

/** The timezone for a country, falling back to India. */
export function getTimezoneForCountry(iso: string): TenantTimezone {
    return COUNTRY_TIMEZONE[iso] ?? COUNTRY_TIMEZONE.IN;
}

/** Every zone a tenant may be set to, for the settings picker. */
export function supportedTimezones(): readonly TenantTimezone[] {
    // Deduplicated by IANA name — several GCC states share an offset but each
    // has its own zone name, and the picker should offer the real ones.
    const seen = new Set<string>();
    return Object.values(COUNTRY_TIMEZONE).filter((tz) => {
        if (seen.has(tz.iana)) return false;
        seen.add(tz.iana);
        return true;
    });
}

/** Is this a zone we know how to reason about without a tz database? */
export function isSupportedTimezone(iana: string): boolean {
    return supportedTimezones().some((tz) => tz.iana === iana);
}

/** Look a zone up by IANA name, falling back to India. */
export function getTimezone(iana: string | null | undefined): TenantTimezone {
    return supportedTimezones().find((tz) => tz.iana === iana) ?? COUNTRY_TIMEZONE.IN;
}

/**
 * `instant` re-expressed as the wall clock in `timeZone`.
 *
 * Returns a Date whose LOCAL getters (`getHours`, `getDay`, `getDate`) read the
 * tenant's clock. It is NOT a valid instant — only its calendar and clock
 * fields are meaningful, and it must never be stored or serialised.
 *
 * This exists because a schedule is a local concept: "17:00-19:00" and "closed
 * on Fridays" mean the business's clock, not the server's and not the cashier's
 * browser's. Both sides of the product need the same answer, so both call this.
 */
export function wallClockIn(timeZone: string, instant: Date = new Date()): Date {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(instant);
    const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
    // Month is 0-based in the Date constructor; hour 24 means midnight.
    return new Date(
        get('year'),
        get('month') - 1,
        get('day'),
        get('hour') % 24,
        get('minute'),
        get('second'),
    );
}

/**
 * The country a zone belongs to — the inverse of COUNTRY_TIMEZONE (T15). A
 * store has no country column; its zone (own, else the tenant's) says where it
 * trades, which is what the weekend default below keys on.
 */
export function getCountryForTimezone(iana: string | null | undefined): string {
    return Object.keys(COUNTRY_TIMEZONE).find((iso) => COUNTRY_TIMEZONE[iso].iana === iana) ?? 'IN';
}

/**
 * Default working days per country, 0 = Sunday … 6 = Saturday (the shifts
 * `days_of_week` convention). Owner decision T15: GCC stores are off Friday and
 * Saturday. India keeps Mon–Fri, the shifts table's existing default.
 */
export const COUNTRY_WORK_DAYS: Readonly<Record<string, readonly number[]>> = Object.freeze({
    IN: Object.freeze([1, 2, 3, 4, 5]),
    AE: Object.freeze([0, 1, 2, 3, 4]),
    OM: Object.freeze([0, 1, 2, 3, 4]),
    SA: Object.freeze([0, 1, 2, 3, 4]),
    QA: Object.freeze([0, 1, 2, 3, 4]),
    KW: Object.freeze([0, 1, 2, 3, 4]),
    BH: Object.freeze([0, 1, 2, 3, 4]),
});

/** Working days for a store on this zone, e.g. Asia/Muscat → Sun–Thu. */
export function getWorkDaysForTimezone(iana: string | null | undefined): number[] {
    return [...(COUNTRY_WORK_DAYS[getCountryForTimezone(iana)] ?? COUNTRY_WORK_DAYS.IN)];
}
